import { AxiosError } from "axios";
import { createGuestApiAxiosClient } from "@/shared/axios/axios";
import type { RegistrationValidationsRequest } from "@/shared/generated/ExpHubGuestApi";
import { translateError } from "@/shared/helper/translateError.helper";
import { i18n } from "@/shared/i18n/i18n";
import type { PreRegistrationValidationData } from "@/app/registration-app/user/shared/@types/PreRegistrationValidationData";

// The information whether a customer is managed is returned by the backend as an error.
// It should not be handled as an error in the frontend, therefore it is checked separately.
const IS_MANAGED_ACCOUNT_ERROR_TYPE = "REGISTRATION:RESTRICTED";

const checkIfCustomerIsManaged = (error: AxiosError | unknown): boolean => {
  if (!(error instanceof AxiosError) || !error.response?.data) {
    return false;
  }

  const { type } = error.response.data;
  return type === IS_MANAGED_ACCOUNT_ERROR_TYPE;
};

// Pre-registration validation errors returned by the backend. These are used to provide specific feedback to the user.
enum PreRegistrationValidationError {
  invalidCustomerId = "User not entitled for the expected sales organization",
  invalidDocumentId = "Invalid document",
  existingEmail = "Email already registered",
  blacklistedCustomer = "Unable to register the given customer", // Some customers are not allowed to register through the portal.
}

const mapErrorToMessage = (error: AxiosError | unknown) => {
  if (!(error instanceof AxiosError) || !error.response?.data?.detail) {
    return translateError("INTERNAL_ERROR");
  }
  const errorDetails = error.response.data.detail;
  for (const [key, value] of Object.entries(PreRegistrationValidationError)) {
    if (errorDetails.includes(value)) {
      return i18n.t(`registration.user.error.${key}`);
    }
  }

  return translateError("INTERNAL_ERROR");
};

export const serializeDataForValidatePreRegistrationData = ({
  customerId,
  email,
  documentId,
  channel,
}: PreRegistrationValidationData): RegistrationValidationsRequest => ({
  channel,
  ERPCustomerId: customerId,
  email,
  // The document number is only known once the customer has been asked for
  // it - the validations endpoint treats it as optional and only performs
  // document validation when it's present.
  ...(documentId ? { referenceNumber: documentId } : {}),
});

export const validatePreRegistrationData = async (
  data: RegistrationValidationsRequest,
): Promise<boolean> => {
  const channel = data.channel.toLowerCase();

  try {
    const client = await createGuestApiAxiosClient();
    await client.post(`/${channel}/registrations/validations`, data);
    return false;
  } catch (error) {
    const isManagedCustomer = checkIfCustomerIsManaged(error);
    if (isManagedCustomer) {
      return isManagedCustomer;
    }

    throw mapErrorToMessage(error);
  }
};
